import type {
    CrossDockParty,
    FallbackRoute,
    FirstTransportOrder,
    FreightForwarder,
    HandlingLocation,
    LoadingLocation,
    PlaceOfDischarge,
    PlaceOfLoading,
    PlantParty,
    RegularRoute,
    Route,
    Shipment,
    ShippingParty,
    ShippingPartyAddress,
    Status,
    StatusLocation,
    SupplierParty,
    TransportLeg,
    TransportLegParty,
    UnloadingLocation,
} from '../../domain/model/shipment/Shipment.types';
import type {
    ApiCrossDockParty,
    ApiFallbackRoute,
    ApiFirstTransportOrder,
    ApiFreightForwarder,
    ApiHandlingLocation,
    ApiLoadingLocation,
    ApiPlaceOfDischarge,
    ApiPlaceOfLoading,
    ApiPlantParty,
    ApiRegularRoute,
    ApiRoute,
    ApiShipment,
    ApiShipmentDateTypeQuery,
    ApiShippingParty,
    ApiShippingPartyAddress,
    ApiStatus,
    ApiStatusLocation,
    ApiSupplierParty,
    ApiTimeWindow,
    ApiTransportLeg,
    ApiTransportLegParty,
    ApiUnloadingLocation,
} from './transportExecution.shipments.schema';
import type { TimeWindow } from '../../domain/model/transport/Transport.types';
import type { ShipmentDateType } from '../../features/shipments-overview/redux/shipmentsOverview.slice';

export const toApiStatus = (src: Status): ApiStatus => src;

const toShippingParty = (src: ApiShippingParty): ShippingParty => ({
    duns: src.duns,
    supplierCode: src.supplier_code ?? undefined,
});

const toShippingPartyAddress = (src: ApiShippingPartyAddress): ShippingPartyAddress => ({
    street: src.street,
    city: src.city,
    postalCode: src.postal_code,
    countryCode: src.country_code,
});

const toPlantParty = (src: ApiPlantParty): PlantParty => ({
    type: 'PLANT',
    duns: src.duns,
    supplierCode: src.supplier_code ?? undefined,
    name: src.name,
    address: toShippingPartyAddress(src.address),
    plantCode: src.plant_code,
    handlingLocation: src.handling_location ? toHandlingLocation(src.handling_location) : undefined,
});

const toSupplierParty = (src: ApiSupplierParty): SupplierParty => ({
    type: 'SUPPLIER',
    duns: src.duns,
    supplierCode: src.supplier_code,
    name: src.name,
    address: toShippingPartyAddress(src.address),
    handlingLocation: src.handling_location ? toHandlingLocation(src.handling_location) : undefined,
});

const toCrossDockParty = (src: ApiCrossDockParty): CrossDockParty => ({
    type: 'CROSS_DOCK',
    duns: src.duns,
    name: src.name,
    address: toShippingPartyAddress(src.address),
});

const toTransportLegParty = (src: ApiTransportLegParty): TransportLegParty => {
    switch (src.type) {
        case 'PLANT':
            return toPlantParty(src);
        case 'CROSS_DOCK':
            return toCrossDockParty(src);
        case 'SUPPLIER':
            return toSupplierParty(src);
    }
};

const toHandlingLocation = (src: ApiHandlingLocation): HandlingLocation => ({
    id: src.id,
    companyName: src.company_name ?? undefined,
    duns: src.duns ?? undefined,
    supplierCode: src.supplier_code ?? undefined,
    address: toShippingPartyAddress(src.address),
});

const toPlaceOfDischarge = (src: ApiPlaceOfDischarge): PlaceOfDischarge => ({
    id: src.id,
});

const toPlaceOfLoading = (src: ApiPlaceOfLoading): PlaceOfLoading => ({
    id: src.id,
});

const toLoadingLocation = (src: ApiLoadingLocation): LoadingLocation => ({
    shipFrom: {
        id: src.ship_from.id,
    },
    placeOfLoading: src.place_of_loading ? toPlaceOfLoading(src.place_of_loading) : undefined,
});

const apiUnloadingLocationToDomain = (src: ApiUnloadingLocation): UnloadingLocation => ({
    shipTo: {
        id: src.ship_to.id,
    },
    placeOfDischarge: src.place_of_discharge ? toPlaceOfDischarge(src.place_of_discharge) : undefined,
});

const toFreightForwarder = (src: ApiFreightForwarder): FreightForwarder => ({
    duns: src.duns,
    supplierCode: src.supplier_code ?? undefined,
    name: src.name ?? undefined,
    address: src.address ? toShippingPartyAddress(src.address) : undefined,
});

const toStatusLocation = (src: ApiStatusLocation): StatusLocation => {
    if (src.physical_location) {
        return {
            physicalLocation: {
                name: src.physical_location.name,
                city: src.physical_location.city,
                countryCode: src.physical_location.country_code,
            },
        };
    }

    return {
        duns: src.duns,
    };
};

const toTransportLeg = (src: ApiTransportLeg): TransportLeg => ({
    transportLegId: src.transport_leg_id,
    transportLegNumber: src.transport_leg_number,
    loading: {
        type: src.loading.type,
        party: toTransportLegParty(src.loading.party),
    },
    unloading: {
        type: src.unloading.type,
        party: toTransportLegParty(src.unloading.party),
    },
    freightForwarder: toFreightForwarder(src.freight_forwarder),
    deliveryDate: src.delivery_date,
    pickupDate: src.pickup_date,
});

const toRegularRoute = (src: ApiRegularRoute): RegularRoute => ({
    type: src.type,
    routeId: src.route_id,
    transportLegs: src.transport_legs
        .map(toTransportLeg)
        .sort((first, second) => first.transportLegNumber - second.transportLegNumber),
});

const toFallbackRoute = (src: ApiFallbackRoute): FallbackRoute => ({
    type: src.type,
    consignor: toShippingParty(src.consignor),
    consignee: toShippingParty(src.consignee),
    freightForwarder: toFreightForwarder(src.freight_forwarder),
});

const apiRouteToDomain = (src: ApiRoute): Route => {
    switch (src.type) {
        case 'REGULAR':
            return toRegularRoute(src);
        case 'FALLBACK':
            return toFallbackRoute(src);
    }
};

const toFirstTransportOrder = (src: ApiFirstTransportOrder): FirstTransportOrder => ({
    transportOrderNumber: src.transport_order_number,
    requestedPickupAt: toTimeWindow(src.requested_pickup_at),
    externalBundleId: src.external_bundle_id ?? undefined,
    accessCode: src.access_code ?? undefined,
});

const toTimeWindow = (src: ApiTimeWindow): TimeWindow => ({
    from: src.from,
    to: src.to,
});

export const toShipment = (src: ApiShipment): Shipment => ({
    id: src.id,
    shipmentNumber: src.shipment_number,
    packagingId: src.packaging_id,
    status: src.status,
    statusLocation: toStatusLocation(src.status_location),
    hasDeviations: src.has_deviations,
    consignor: toShippingParty(src.consignor),
    consignee: toShippingParty(src.consignee),
    loadingLocation: toLoadingLocation(src.loading_location),
    unloadingLocation: apiUnloadingLocationToDomain(src.unloading_location),
    route: apiRouteToDomain(src.route),
    calculatedDeliveryTimeAt: src.calculated_delivery_time_at ?? undefined,
    pickupConfirmedAt: src.pickup_confirmed_at,
    firstTransportOrder: src.first_transport_order ? toFirstTransportOrder(src.first_transport_order) : undefined,
});

export const toApiShipmentDateTypeQuery = (src: ShipmentDateType): ApiShipmentDateTypeQuery => src;
